import { DATA_SOURCES, SINGULAR_NAMES, isCovered } from 'config/constants';
import { shapeDataPoint } from 'state/utils';
import { shapeFlavors } from 'utils/helpers';
import { indexEntities } from 'utils/entities';
import { fetchEntityData, fetchEntityDataPairs } from 'utils/api/entities.api';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import useCurrentCountry from './useCurrentCountry';
import useGetHierarchy from './useGetHierarchy';
import useSeedEntityCache from './useSeedEntityCache';

/** What an entity contributes to a merged row; the data point supplies the rest. */
const PRESERVED_ENTITY_KEYS = [
    'id',
    'name',
    'fetching',
    'type',
    'depth',
    'children',
    'parent_id',
    'parents',
    'kw',
    'brands',
    'affinities',
    'products',
    'groups',
    'cat_fives',
    'hashtags',
    'translations',
    'hierarchy_hint',
    'hierarchies',
    'image',
    'code',
    'text_id',
    'internet_using_population',
    'category_id',
    'is_hidden_root',
];

/** Which entity each row points at; shapeDataPoint does not carry these. */
const ID_COLUMNS = Object.values(SINGULAR_NAMES);

/** Shape in the query, so it happens once per fetch rather than once per render. */
const shapeRows = ({ rows, ...rest }) => ({
    ...rest,
    rows: (rows || []).map((row) => ({
        // shapeDataPoint drops the dimension columns, but the merge indexes
        // rows by them — keep them, and let the shaped fields win on `id`.
        ...ID_COLUMNS.reduce((acc, column) => {
            if (row[column]) acc[column] = row[column];
            return acc;
        }, {}),
        ...shapeDataPoint(row),
    })),
});

const makeQueryFn =
    (controller) =>
    ({ queryKey }) => {
        const { data, paginateBy, minimal } = queryKey[1];
        return fetchEntityData({
            data,
            paginateBy,
            controller,
            minimal,
        }).then(shapeRows);
    };

/**
 * Google data_points joined to their entities, coverage judged against Google.
 * For any other source use `useEntity` / `useEntities`, which take a
 * `dataSource`. See frontend/CONTRIBUTING.md, "Entities and Data Sources".
 *
 * `includeNoDataItems` keeps rows whose entity resolved but which have no data
 * point: the row IS the entity for a caller reading a name or hierarchy off it.
 * It carries no invented metrics. Category-paginated tables opt out.
 */
const useGoogleData = ({
    data,
    paginateBy,
    minimal = true,
    includeNoDataItems = true,
    enabled = true,
    controller = null,
    ...props
}) => {
    const getHierarchy = useGetHierarchy();
    const geo = useCurrentCountry();
    const idList = paginateBy && data ? data[paginateBy] : null;
    // Gates the query only. The merge treats an empty list as an answer, not as
    // "still loading" -- see CONTRIBUTING, "An empty id list is an answer".
    const hasIds = !!idList?.length;
    // Call sites build `data` fresh each render; memoise on its shape.
    const dataKey = JSON.stringify(data);

    const query = useQuery({
        queryKey: ['entity-data', { data, paginateBy, minimal }],
        queryFn: makeQueryFn(controller),
        enabled: enabled && hasIds,
        staleTime: Infinity,
        ...props,
    });
    useSeedEntityCache(query.data?.entities, DATA_SOURCES.GOOGLE);

    const merged = useMemo(() => {
        if (!idList) return null;
        if (!idList.length) return [];
        if (!query.data) return null;

        const entities = indexEntities(query.data.entities);
        const idKey = SINGULAR_NAMES[paginateBy];
        const rowsById = (query.data.rows || []).reduce((acc, row) => {
            acc[row[idKey]] = row;
            return acc;
        }, {});

        const countryId = data.countries?.[0];
        const requestCountry =
            geo?.id === countryId ? geo : entities[countryId];
        const anchorCategoryId = data.categories?.[0];

        return data[paginateBy].reduce((acc, id) => {
            const entity = entities[id];
            const category =
                paginateBy === 'categories'
                    ? entity
                    : entities[anchorCategoryId];
            // A country-paginated table is one category across many markets, so
            // coverage is judged per row rather than on the request's country.
            const country =
                paginateBy === 'countries' ? entity : requestCountry;
            const itemData = rowsById[id];

            if (!includeNoDataItems && !itemData) return acc;

            const cat1 = category?.hierarchies
                ? getHierarchy(category.hierarchies).hierarchy_hint?.cat1
                : null;
            const isRedactedCategory =
                category &&
                country &&
                cat1 &&
                !isCovered({
                    geo: country.text_id,
                    dataSource: DATA_SOURCES.GOOGLE,
                    cat1,
                });

            const parentId =
                entity?.parent_id || getHierarchy(entity?.hierarchies).parent;
            const parent = parentId ? entities[parentId] : null;

            acc.push({
                id,
                ...(!isRedactedCategory && itemData),
                ...(parent && { parentName: parent.name }),
                ...(entity &&
                    PRESERVED_ENTITY_KEYS.reduce((kept, key) => {
                        if (entity.hasOwnProperty(key)) kept[key] = entity[key];
                        return kept;
                    }, {})),
                flavors: shapeFlavors(entity?.hierarchies),
                fetching: false,
                fetchingData: false,
            });
            return acc;
        }, []);
    }, [
        query.data,
        dataKey,
        paginateBy,
        includeNoDataItems,
        getHierarchy,
        geo,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    return { ...query, data: merged };
};

const PAIRED_KEYS = ['categories', 'brands'];

const makePairsQueryFn =
    (controller) =>
    ({ queryKey }) => {
        const { data, paginateBy } = queryKey[1];
        return fetchEntityDataPairs({
            data,
            paginateBy,
            pairedKeys: PAIRED_KEYS,
            controller,
        }).then(shapeRows);
    };

/**
 * Data points for index-aligned pairs — an affinity paired with the category or
 * brand it was saved against, which is how Google watchlists store them.
 *
 * Google-only: affinities predate the per-source entity model and are slated to
 * become related_categories, the way TikTok and Instagram already work.
 *
 * `includeNoDataItems` means the same thing it does in `useGoogleData`, and the
 * Google watchlist — the only caller — needs it: a pair the user saved is still
 * theirs to see and remove in a market where it has no data point.
 */
export const useGoogleDataMapped = ({
    data,
    paginateBy,
    enabled = true,
    controller = null,
    get_parent_trend_name = false,
    includeNoDataItems = true,
    ...props
}) => {
    const hasIds = !!(paginateBy && data && data[paginateBy]?.length);
    const dataKey = JSON.stringify(data);

    const query = useQuery({
        queryKey: ['entity-data-pairs', { data, paginateBy }],
        queryFn: makePairsQueryFn(controller),
        enabled: enabled && hasIds,
        staleTime: Infinity,
        ...props,
    });

    const merged = useMemo(() => {
        if (!(hasIds && query.data)) return null;

        const entities = indexEntities(query.data.entities);
        const idKey = SINGULAR_NAMES[paginateBy];
        // Keyed on both halves: one affinity can be saved under two categories.
        const rowsByPair = (query.data.rows || []).reduce((acc, row) => {
            acc[`${row[idKey]}||${row.category || row.brand}`] = row;
            return acc;
        }, {});

        return data[paginateBy].reduce((acc, id, index) => {
            const categoryId = data.categories?.[index];
            const brandId = data.brands?.[index];
            const pairedId = brandId || categoryId;
            const itemData = rowsByPair[`${id}||${pairedId}`];
            const entity = entities[id];

            if (!includeNoDataItems && !itemData) return acc;

            const parentTrendName =
                get_parent_trend_name &&
                (itemData?.parent_trend_name || entities[pairedId]?.name);

            acc.push({
                id,
                ...itemData,
                ...(entity &&
                    PRESERVED_ENTITY_KEYS.reduce((kept, key) => {
                        if (entity.hasOwnProperty(key)) kept[key] = entity[key];
                        return kept;
                    }, {})),
                flavors: shapeFlavors(entity?.hierarchies),
                fetching: false,
                fetchingData: false,
                ...(brandId && { brandId }),
                ...(categoryId && { categoryId }),
                ...(parentTrendName && { parent_trend_name: parentTrendName }),
            });
            return acc;
        }, []);
    }, [
        query.data,
        dataKey,
        paginateBy,
        get_parent_trend_name,
        includeNoDataItems,
        hasIds,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    return { ...query, data: merged };
};

export default useGoogleData;
